import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Text,
  HStack,
  Button,
  IconButton,
} from 'src/components/ui';
import { CloseIcon } from '@chakra-ui/icons';
import { FaMousePointer } from 'react-icons/fa';
import {
  TOP_BAR_HEIGHT,
  RIGHT_SIDEBAR_WIDTH_COLLAPSED,
} from 'src/components/campaigns/utils/constants';

// Confetti animation function using canvas - burst effect from bottom corners
const launchConfetti = () => {
  const canvas = document.createElement('canvas');
  canvas.style.position = 'fixed';
  canvas.style.top = '0';
  canvas.style.left = '0';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.pointerEvents = 'none';
  canvas.style.zIndex = '99999';
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const colors = ['#FF6B35', '#F7931E', '#FFD700', '#FF4500', '#FFA500', '#32CD32', '#4169E1', '#9370DB', '#FF69B4'];
  const confettiCount = 200;
  const confetti: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    color: string;
    size: number;
    rotation: number;
    rotationSpeed: number;
    shape: 'rect' | 'circle' | 'star';
    wobble: number;
    wobbleSpeed: number;
  }> = [];

  // Create confetti bursting from bottom-left and bottom-right corners
  for (let i = 0; i < confettiCount; i++) {
    // Alternate between left and right cannon
    const isLeftCannon = i % 2 === 0;
    const startX = isLeftCannon ? canvas.width * 0.1 : canvas.width * 0.9;
    const startY = canvas.height;

    // Angle spread: left cannon shoots right-upward, right cannon shoots left-upward
    const baseAngle = isLeftCannon ? -70 : -110; // degrees from horizontal
    const angleSpread = 50; // degrees of spread
    const angle = (baseAngle + (Math.random() - 0.5) * angleSpread) * (Math.PI / 180);

    // Random velocity for variety
    const velocity = 8 + Math.random() * 8;

    confetti.push({
      x: startX,
      y: startY,
      vx: Math.cos(angle) * velocity,
      vy: Math.sin(angle) * velocity,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: Math.random() * 10 + 5,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 8,
      shape: ['rect', 'circle', 'star'][Math.floor(Math.random() * 3)] as 'rect' | 'circle' | 'star',
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: 0.05 + Math.random() * 0.05,
    });
  }

  let animationFrame: number;
  let startTime = Date.now();
  const duration = 7000; // 7 seconds for slower effect

  const drawStar = (ctx: CanvasRenderingContext2D, size: number) => {
    const spikes = 5;
    const outerRadius = size / 2;
    const innerRadius = size / 4;

    ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (i * Math.PI) / spikes - Math.PI / 2;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  };

  const animate = () => {
    const elapsed = Date.now() - startTime;
    if (elapsed > duration) {
      cancelAnimationFrame(animationFrame);
      document.body.removeChild(canvas);
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    confetti.forEach((particle) => {
      // Update wobble for swaying motion
      particle.wobble += particle.wobbleSpeed;

      // Apply physics with wobble
      particle.x += particle.vx + Math.sin(particle.wobble) * 0.5;
      particle.y += particle.vy;
      particle.vy += 0.15; // gentle gravity
      particle.vx *= 0.995; // slight air resistance
      particle.rotation += particle.rotationSpeed;

      // Fade out in the last 40% of duration
      const fadeStart = duration * 0.6;
      const opacity = elapsed > fadeStart
        ? Math.max(0, 1 - (elapsed - fadeStart) / (duration - fadeStart))
        : 1;

      ctx.save();
      ctx.translate(particle.x, particle.y);
      ctx.rotate((particle.rotation * Math.PI) / 180);
      ctx.fillStyle = particle.color;
      ctx.globalAlpha = opacity;

      if (particle.shape === 'rect') {
        ctx.fillRect(-particle.size / 2, -particle.size / 4, particle.size, particle.size / 2);
      } else if (particle.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, particle.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        drawStar(ctx, particle.size);
      }

      ctx.restore();
    });

    animationFrame = requestAnimationFrame(animate);
  };

  animate();
};

// Tour step definitions for Step 3 (Finalize Tour)
// 3 steps:
// 1. Schedule section - explain about end date
// 2. Budget input - explain it's total campaign budget
// 3. Publish button - congratulations message
export const FINALIZE_TOUR_STEPS = {
  SCHEDULE_SECTION: 0,      // Highlight Schedule section with end date explanation
  BUDGET_INPUT: 1,          // Highlight Budget input with distribution explanation
  PUBLISH_BUTTON: 2,        // Final step - congratulations and publish
  COMPLETE: 3,              // Tour complete
};

interface FinalizeTourProps {
  isActive: boolean;
  currentStep: number;
  onComplete: () => void;
  onSkip: () => void;
  onAdvance: () => void;
  setStep: (step: number) => void;
}

interface TooltipPosition {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

const FinalizeTour: React.FC<FinalizeTourProps> = ({
  isActive,
  currentStep,
  onComplete,
  onSkip,
  onAdvance,
  setStep,
}) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({});
  const [isVisible, setIsVisible] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [showClickEffect, setShowClickEffect] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const observerRef = useRef<MutationObserver | null>(null);
  const prevStepRef = useRef<number>(-1);
  const hasScrolledForStepRef = useRef<number>(-1);

  // Check if current step allows full interaction (no overlay at all)
  // Only PUBLISH_BUTTON allows full interaction for navigation
  const isInteractiveStep = currentStep === FINALIZE_TOUR_STEPS.PUBLISH_BUTTON;

  // Check if step allows clicks to pass through overlay (for date pickers)
  // SCHEDULE_SECTION shows overlay but allows clicks so date pickers work
  const allowClicksThrough = currentStep === FINALIZE_TOUR_STEPS.SCHEDULE_SECTION;

  // Check if final step (for arrow display)
  const isFinalStep = currentStep === FINALIZE_TOUR_STEPS.PUBLISH_BUTTON;

  // Check individual steps for separate tooltip components
  const isScheduleStep = currentStep === FINALIZE_TOUR_STEPS.SCHEDULE_SECTION;
  const isBudgetStep = currentStep === FINALIZE_TOUR_STEPS.BUDGET_INPUT;

  // Z-index values
  const baseZIndex = 9990;
  const overlayZIndex = baseZIndex;
  const borderZIndex = baseZIndex + 2;
  const badgeZIndex = baseZIndex + 8;
  const tooltipZIndex = baseZIndex + 8;
  const cursorZIndex = baseZIndex + 9;
  const headerOverlayZIndex = 10202;

  // Get target element based on current step
  const getTargetSelector = useCallback(() => {
    switch (currentStep) {
      case FINALIZE_TOUR_STEPS.SCHEDULE_SECTION:
        return '[data-tour="schedule-section"]';
      case FINALIZE_TOUR_STEPS.BUDGET_INPUT:
        return '[data-tour="budget-input"]';
      case FINALIZE_TOUR_STEPS.PUBLISH_BUTTON:
        return '[data-tour="publish-button"]';
      default:
        return null;
    }
  }, [currentStep]);

  // Get step content
  const getStepContent = useCallback(() => {
    switch (currentStep) {
      case FINALIZE_TOUR_STEPS.SCHEDULE_SECTION:
        return {
          title: 'Set Your Campaign Schedule',
          description: 'If you don\'t set an End Date, your campaign will run until your budget runs out. To control when your campaign ends, toggle "Define an end date" and select your preferred date.',
        };
      case FINALIZE_TOUR_STEPS.BUDGET_INPUT:
        return {
          title: 'Set Your Campaign Budget',
          description: 'This is the total budget for your campaign. It will be automatically distributed across the channels you selected. Once you enter an amount, you\'ll see the breakdown and can adjust it based on your preferences.',
        };
      case FINALIZE_TOUR_STEPS.PUBLISH_BUTTON:
        return {
          title: 'Congratulations!',
          description: 'You\'ve completed all the steps! You may need to connect some ad accounts, but you\'re ready to publish your campaign. Click here when you\'re ready to go live!',
        };
      default:
        return { title: '', description: '' };
    }
  }, [currentStep]);

  const TOTAL_DISPLAY_STEPS = 3;

  // Scroll element into view with proper positioning
  const scrollElementIntoView = useCallback((element: Element) => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Target position: element should be about 30% from top of viewport
    const targetY = viewportHeight * 0.30;
    const currentY = rect.top;
    const scrollNeeded = currentY - targetY;

    // Always scroll if the element is below 50% of viewport or above 20%
    const shouldScroll = currentY > viewportHeight * 0.5 || currentY < viewportHeight * 0.2;

    if (shouldScroll && Math.abs(scrollNeeded) > 20) {
      window.scrollBy({
        top: scrollNeeded,
        behavior: 'smooth'
      });
    }
  }, []);

  // Update target element position
  const updateTargetPosition = useCallback(() => {
    const selector = getTargetSelector();
    if (!selector) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(selector);
    if (element) {
      // Scroll element into view on first time for each step
      if (hasScrolledForStepRef.current !== currentStep) {
        hasScrolledForStepRef.current = currentStep;
        scrollElementIntoView(element);
      }

      const rect = element.getBoundingClientRect();
      setTargetRect(rect);
      setIsVisible(true);

      // Calculate tooltip position based on available space
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      if (currentStep === FINALIZE_TOUR_STEPS.PUBLISH_BUTTON) {
        // For Publish button, always position tooltip ABOVE the button with arrow
        setTooltipPosition({
          top: Math.max(100, rect.top - 210),
          left: Math.max(20, Math.min(rect.left - 100, viewportWidth - 380)),
        });
      } else if (currentStep === FINALIZE_TOUR_STEPS.SCHEDULE_SECTION) {
        // For Schedule section, position tooltip clearly BELOW and to the LEFT
        // to avoid overlapping with the date inputs
        setTooltipPosition({
          top: rect.bottom + 30,
          left: Math.max(20, rect.left - 50),
        });
      } else if (rect.bottom + 200 > viewportHeight) {
        // Position above if not enough space below
        setTooltipPosition({
          top: Math.max(100, rect.top - 180),
          left: Math.max(20, Math.min(rect.left, viewportWidth - 380)),
        });
      } else {
        // Position below the target
        setTooltipPosition({
          top: rect.bottom + 16,
          left: Math.max(20, Math.min(rect.left, viewportWidth - 380)),
        });
      }

      // Update cursor position - point to a clickable area
      if (currentStep === FINALIZE_TOUR_STEPS.SCHEDULE_SECTION) {
        // Point to the "Define an end date" checkbox
        // Find the actual checkbox element for precise positioning
        const endDateCheckbox = document.querySelector('[data-tour="define-end-date"]') ||
                                document.querySelector('input[type="checkbox"]');
        if (endDateCheckbox) {
          const checkboxRect = endDateCheckbox.getBoundingClientRect();
          setCursorPosition({
            x: checkboxRect.left + checkboxRect.width / 2 - 12,
            y: checkboxRect.top + checkboxRect.height / 2 - 12,
          });
        } else {
          // Fallback: position at top-right where checkbox should be
          setCursorPosition({
            x: rect.right - 280,
            y: rect.top + 20,
          });
        }
      } else if (currentStep === FINALIZE_TOUR_STEPS.BUDGET_INPUT) {
        // Point to the budget input field
        setCursorPosition({
          x: rect.left + rect.width / 2 - 12,
          y: rect.top + rect.height / 2 - 12,
        });
      } else {
        // Default: center of element
        setCursorPosition({
          x: rect.left + rect.width / 2 - 12,
          y: rect.top + rect.height / 2 - 12,
        });
      }
    } else {
      setTargetRect(null);
      setIsVisible(false);
    }
  }, [getTargetSelector, currentStep, scrollElementIntoView]);

  // Handle step transitions
  useEffect(() => {
    if (prevStepRef.current !== currentStep) {
      // Launch confetti when transitioning from BUDGET_INPUT (step 1) to PUBLISH_BUTTON (step 2)
      if (prevStepRef.current === FINALIZE_TOUR_STEPS.BUDGET_INPUT &&
          currentStep === FINALIZE_TOUR_STEPS.PUBLISH_BUTTON) {
        launchConfetti();
      }

      setIsTransitioning(true);
      setTimeout(() => {
        setIsTransitioning(false);
        prevStepRef.current = currentStep;
      }, 200);
    }
  }, [currentStep]);

  // Reset scroll tracking when tour becomes inactive
  useEffect(() => {
    if (!isActive) {
      hasScrolledForStepRef.current = -1;
      prevStepRef.current = -1;
    }
  }, [isActive]);

  // Block scroll when tour is active (except for interactive/final step)
  useEffect(() => {
    if (isActive && !isInteractiveStep) {
      // Save current scroll position and block scroll
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isActive, isInteractiveStep]);

  // Watch for DOM changes to find elements that might appear later
  useEffect(() => {
    if (!isActive) return;

    // Initial check with delay to allow DOM to settle
    const initialTimeout = setTimeout(() => {
      updateTargetPosition();
    }, 300);

    // Set up mutation observer to watch for element appearing
    observerRef.current = new MutationObserver(() => {
      updateTargetPosition();
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Also update on scroll/resize
    const handleUpdate = () => updateTargetPosition();
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    // Periodic update to catch dynamic changes
    const interval = setInterval(updateTargetPosition, 500);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      observerRef.current?.disconnect();
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [isActive, currentStep, updateTargetPosition]);

  // Show click effect periodically
  useEffect(() => {
    if (!isActive || !isVisible) return;

    const interval = setInterval(() => {
      setShowClickEffect(true);
      setTimeout(() => setShowClickEffect(false), 500);
    }, 3000);

    // Show immediately once
    setTimeout(() => {
      setShowClickEffect(true);
      setTimeout(() => setShowClickEffect(false), 500);
    }, 500);

    return () => clearInterval(interval);
  }, [isActive, isVisible, currentStep]);

  // Listen for click on Publish button to complete the tour
  useEffect(() => {
    if (!isActive || currentStep !== FINALIZE_TOUR_STEPS.PUBLISH_BUTTON) return;

    const handlePublishClick = () => {
      onComplete();
    };

    const publishButton = document.querySelector('[data-tour="publish-button"]');
    if (publishButton) {
      publishButton.addEventListener('click', handlePublishClick);
      return () => {
        publishButton.removeEventListener('click', handlePublishClick);
      };
    }
  }, [isActive, currentStep, onComplete]);

  // Handle button click
  const handleButtonClick = () => {
    onAdvance();
  };


  if (!isActive) {
    return null;
  }

  if (!isVisible || !targetRect) return null;

  const padding = 12;
  const rect = {
    left: targetRect.left - padding,
    top: targetRect.top - padding,
    right: targetRect.right + padding,
    bottom: targetRect.bottom + padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
  };

  return createPortal(
    <Box>
      {/* Dark overlay with spotlight cutout - not shown for final step */}
      {/* Final step has no overlay so user can interact with the page */}
      {/* Stops before right sidebar to avoid double overlay */}
      {!isInteractiveStep && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
          bottom={0}
          zIndex={overlayZIndex}
          bg="blackAlpha.600"
          backdropFilter="blur(4px)"
          cursor={allowClicksThrough ? "default" : "not-allowed"}
          pointerEvents={allowClicksThrough ? "none" : "auto"}
          onClick={allowClicksThrough ? undefined : (e) => { e.preventDefault(); e.stopPropagation(); }}
          style={{
            clipPath: isTransitioning ? undefined : `polygon(
              0% 0%,
              0% 100%,
              ${rect.left}px 100%,
              ${rect.left}px ${rect.top}px,
              ${rect.right}px ${rect.top}px,
              ${rect.right}px ${rect.bottom}px,
              ${rect.left}px ${rect.bottom}px,
              ${rect.left}px 100%,
              100% 100%,
              100% 0%
            )`,
          }}
        />
      )}

      {/* Dark overlay for TopBar - same style as rest of overlay */}
      {!isInteractiveStep && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
          h={TOP_BAR_HEIGHT}
          zIndex={headerOverlayZIndex}
          bg="blackAlpha.600"
          backdropFilter="blur(4px)"
          pointerEvents="none"
        />
      )}

      {/* Dark overlay for Right Sidebar - covers entire sidebar height */}
      {!isInteractiveStep && (
        <Box
          position="fixed"
          top={0}
          right={0}
          bottom={0}
          w={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
          zIndex={headerOverlayZIndex}
          bg="blackAlpha.600"
          backdropFilter="blur(4px)"
          pointerEvents="none"
        />
      )}

      {/* Transitioning content - spotlight, tooltip, cursor */}
      <Box
        opacity={isTransitioning ? 0 : 1}
        transition="opacity 0.2s ease-in-out"
      >
      {/* Orange border around spotlight */}
      <Box
        position="fixed"
        left={`${rect.left}px`}
        top={`${rect.top}px`}
        width={`${rect.width}px`}
        height={`${rect.height}px`}
        borderRadius="8px"
        zIndex={borderZIndex}
        pointerEvents="none"
        border="3px solid"
        borderColor="orange.400"
        boxShadow="0 0 0 3px rgba(246, 133, 30, 0.25)"
      />

      {/* Step number badge - top left corner */}
      <Box
        position="fixed"
        left={`${rect.left - 12}px`}
        top={`${rect.top - 12}px`}
        zIndex={badgeZIndex}
        bg="orange.500"
        color="white"
        borderRadius="full"
        w="28px"
        h="28px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="sm"
        fontWeight="bold"
        boxShadow="md"
        pointerEvents="none"
      >
        {currentStep + 1}
      </Box>

      {/* Tooltip for step 1/3 (SCHEDULE) - SEPARATE COMPONENT for independent positioning */}
      {isScheduleStep && (
        <Box
          position="fixed"
          left={`${(tooltipPosition.left || 0) + 40}px`}
          top={`${(tooltipPosition.top || 0) + 5}px`}
          zIndex={tooltipZIndex}
          bg="white"
          borderRadius="lg"
          boxShadow="xl"
          p={4}
          maxW="360px"
          pointerEvents="auto"
        >
          <HStack justify="space-between" align="flex-start" mb={2}>
            <Text fontWeight="bold" fontSize="md">
              {getStepContent().title}
            </Text>
            <IconButton
              aria-label="Close tour"
              icon={<CloseIcon boxSize={2.5} />}
              size="xs"
              variant="ghost"
              onClick={onSkip}
            />
          </HStack>
          <Text color="gray.600" fontSize="sm" mb={4}>
            {getStepContent().description}
          </Text>
          <HStack justify="space-between">
            <Button size="sm" variant="ghost" onClick={onSkip}>
              Skip
            </Button>
            <HStack spacing={3}>
              <Text fontSize="xs" color="gray.500">
                Step {currentStep + 1} of {TOTAL_DISPLAY_STEPS}
              </Text>
              <Button size="sm" colorScheme="orange" onClick={handleButtonClick}>
                Next
              </Button>
            </HStack>
          </HStack>
        </Box>
      )}

      {/* Upward-pointing arrow for step 1/3 (SCHEDULE) */}
      {isScheduleStep && tooltipPosition.top && targetRect && (
        <Box
          position="fixed"
          top={`${(tooltipPosition.top || 0) - 15}px`}
          left={`${targetRect.left + targetRect.width / 2 - 510}px`}
          zIndex={tooltipZIndex + 1}
          w={0}
          h={0}
          borderLeft="16px solid transparent"
          borderRight="16px solid transparent"
          borderBottom="32px solid white"
          pointerEvents="none"
        />
      )}

      {/* Tooltip for step 2/3 (BUDGET) - SEPARATE COMPONENT for independent positioning */}
      {isBudgetStep && (
        <Box
          position="fixed"
          left={`${(tooltipPosition.left || 0) + 10}px`}
          top={`${(tooltipPosition.top || 0) + 15}px`}
          zIndex={tooltipZIndex}
          bg="white"
          borderRadius="lg"
          boxShadow="xl"
          p={4}
          maxW="360px"
          pointerEvents="auto"
        >
          <HStack justify="space-between" align="flex-start" mb={2}>
            <Text fontWeight="bold" fontSize="md">
              {getStepContent().title}
            </Text>
            <IconButton
              aria-label="Close tour"
              icon={<CloseIcon boxSize={2.5} />}
              size="xs"
              variant="ghost"
              onClick={onSkip}
            />
          </HStack>
          <Text color="gray.600" fontSize="sm" mb={4}>
            {getStepContent().description}
          </Text>
          <HStack justify="space-between">
            <Button size="sm" variant="ghost" onClick={onSkip}>
              Skip
            </Button>
            <HStack spacing={3}>
              <Text fontSize="xs" color="gray.500">
                Step {currentStep + 1} of {TOTAL_DISPLAY_STEPS}
              </Text>
              <Button size="sm" colorScheme="orange" onClick={handleButtonClick}>
                Next
              </Button>
            </HStack>
          </HStack>
        </Box>
      )}

      {/* Upward-pointing arrow for step 2/3 (BUDGET) */}
      {isBudgetStep && tooltipPosition.top && targetRect && (
        <Box
          position="fixed"
          top={`${(tooltipPosition.top || 0) - 5}px`}
          left={`${targetRect.left + targetRect.width / 2 - 490}px`}
          zIndex={tooltipZIndex + 1}
          w={0}
          h={0}
          borderLeft="16px solid transparent"
          borderRight="16px solid transparent"
          borderBottom="32px solid white"
          pointerEvents="none"
        />
      )}

      {/* Tooltip for step 3/3 (PUBLISH) - SEPARATE COMPONENT for independent positioning */}
      {isFinalStep && (
        <Box
          position="fixed"
          left={`${(tooltipPosition.left || 0) + 20}px`}
          top={`${(tooltipPosition.top || 0) - 20}px`}
          zIndex={tooltipZIndex}
          bg="white"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          p={4}
          maxW="360px"
          pointerEvents="auto"
        >
          <HStack justify="space-between" align="flex-start" mb={2}>
            <Text fontWeight="bold" fontSize="md">
              {getStepContent().title}
            </Text>
            <IconButton
              aria-label="Close tour"
              icon={<CloseIcon boxSize={2.5} />}
              size="xs"
              variant="ghost"
              onClick={onSkip}
            />
          </HStack>
          <Text color="gray.600" fontSize="sm" mb={4}>
            {getStepContent().description}
          </Text>
          <HStack justify="space-between">
            <Button size="sm" variant="ghost" onClick={onSkip}>
              Skip
            </Button>
            <HStack spacing={3}>
              <Text fontSize="xs" color="gray.500">
                Step {currentStep + 1} of {TOTAL_DISPLAY_STEPS}
              </Text>
            </HStack>
          </HStack>
        </Box>
      )}

      {/* Downward-pointing arrow for step 3/3 (PUBLISH) */}
      {isFinalStep && tooltipPosition.top && targetRect && (
        <Box
          position="fixed"
          top={`${(tooltipPosition.top || 0) + 170}px`}
          left={`${targetRect.left + targetRect.width / 2 - 16}px`}
          zIndex={tooltipZIndex + 1}
          w={0}
          h={0}
          borderLeft="16px solid transparent"
          borderRight="16px solid transparent"
          borderTop="32px solid white"
          pointerEvents="none"
        />
      )}

      {/* Animated cursor */}
      <Box
        position="fixed"
        left={`${cursorPosition.x}px`}
        top={`${cursorPosition.y}px`}
        zIndex={cursorZIndex}
        pointerEvents="none"
        transition="left 1.5s ease, top 1.5s ease"
      >
          <Box
            color="orange.500"
            filter="drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
            transform={showClickEffect ? 'scale(0.85)' : 'scale(1)'}
            transition="transform 0.1s"
          >
            <FaMousePointer size={24} />
          </Box>
          {showClickEffect && (
            <Box
              position="absolute"
              top="50%"
              left="50%"
              w="30px"
              h="30px"
              borderRadius="50%"
              bg="orange.400"
              sx={{
                animation: 'ripple 0.5s ease-out',
                '@keyframes ripple': {
                  '0%': { transform: 'translate(-50%, -50%) scale(0)', opacity: 0.6 },
                  '100%': { transform: 'translate(-50%, -50%) scale(2)', opacity: 0 },
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>,
    document.body
  );
};

export default FinalizeTour;
